/**
 * Mail (docs/features/mail.md): a workspace hosts its own domains (any DNS provider; MX → mx1/mx2.bundle.direct, the
 * Stalwart edge in apps/mx; outbound through Amazon SES), members get mailboxes on them, and every thread lands in a
 * tab chosen by the AI classifier — Email, Clients, Team, Promotions, Updates — like Gmail's category tabs. A contacts
 * list grows from the mail itself and is filtered by the same three kinds (Email / Clients / Team).
 */
import { z } from "zod";
import { RsvpStatus } from "./calendar.ts";
import { EmailAddress, Id, Locale, Timestamp } from "./entities.ts";

export { EmailAddress };

/**
 * The built-in category tabs, the ones the sorting rules know (core logic/mail chooseTab). `email` is mail that fits
 * none of the others (uncategorised); the tab with that id is All, which lists every category too (MailTabDef.listsAll).
 * Which tabs a workspace shows, in what order and how they look comes from the server (MailTabDef), never from clients.
 */
export const MailTab = z.enum(["email", "clients", "team", "promotions", "updates"]);
export type MailTab = z.infer<typeof MailTab>;

/** A tab's id: a built-in category (MailTab), "priority", or one the server's tab settings add. */
export const MailTabId = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/);
export type MailTabId = z.infer<typeof MailTabId>;

/**
 * One tab of the inbox strip as the server defines it (apps/api/src/mail/tabs.json, kept per workspace). Clients draw
 * the strip from these, in order, and cache them, so tabs come and go without an app release.
 */
export const MailTabDef = z.object({
  id: MailTabId,
  /**
   * "priority": Gmail's Priority Inbox, what needs me from every tab (core logic/mail priorityReason); a thread's own
   * `tab` never is. "category": conversations sorted into it. Clients skip kinds they don't know.
   */
  kind: z.enum(["priority", "category"]),
  /** Its name by language ("en", "pt-BR"): clients pick theirs, then the language alone, then "en" (core mailTabLabel). */
  labels: z.record(z.string().max(20), z.string().max(40)),
  /** A glyph of the shared icon font (packages/tokens/icons.json); clients without it draw "tag". */
  icon: z.string().max(40),
  /** Its "N new" and tag tint: "accent" or a tag color (gray, brown, orange, yellow, green, blue, purple, pink, red). */
  color: z.string().max(20),
  /** Mail waiting on my reply in this tab counts for Priority. */
  waitsOnMe: z.boolean().optional(),
  /**
   * A category that also lists the inbox's other tabs (All): conversations sorted into it are the uncategorised ones,
   * and it shows and counts every inbox conversation, whatever its own tab. A flag rather than a kind, because apps
   * from before it leave out kinds they don't know: to them this stays a category.
   */
  listsAll: z.boolean().optional(),
});
export type MailTabDef = z.infer<typeof MailTabDef>;

/** Why a thread is in Priority: I marked it, it's starred, I marked its sender, a reminder came back, or it waits on me. */
export const MailPriorityReason = z.enum(["marked", "starred", "sender", "reminder", "waiting"]);
export type MailPriorityReason = z.infer<typeof MailPriorityReason>;

/** Where a thread sits. Spam is where quarantined mail (DMARC fail, high spam score) goes. */
export const MailFolder = z.enum(["inbox", "sent", "archive", "trash", "spam"]);
export type MailFolder = z.infer<typeof MailFolder>;

/** A contact's kind: the three filters of the contacts list. */
export const ContactKind = z.enum(["email", "client", "team"]);
export type ContactKind = z.infer<typeof ContactKind>;

export const MailAddress = z.object({ address: EmailAddress, name: z.string().max(200).optional() });
export type MailAddress = z.infer<typeof MailAddress>;

/** Authentication results from the edge (SPF/DKIM/DMARC) and its spam score. */
export const AuthResult = z.enum(["pass", "fail", "softfail", "neutral", "none", "temperror", "permerror"]);
export type AuthResult = z.infer<typeof AuthResult>;
export const MailAuth = z.object({
  spf: AuthResult,
  dkim: AuthResult,
  dmarc: AuthResult,
  spamScore: z.number().optional(),
});
export type MailAuth = z.infer<typeof MailAuth>;

export const MailAttachment = z.object({
  id: Id,
  filename: z.string().max(255),
  contentType: z.string().max(255),
  size: z.number().int().nonnegative(),
  /** Shown in the body (`cid:` image), not in the attachment list. */
  inline: z.boolean(),
});
export type MailAttachment = z.infer<typeof MailAttachment>;

/** A guest on a calendar invite (its ATTENDEE lines) and their answer as the invite tells it. */
export const MailInviteGuest = z.object({ address: EmailAddress, name: z.string().max(200).optional(), response: RsvpStatus, optional: z.boolean().optional() });
export type MailInviteGuest = z.infer<typeof MailInviteGuest>;

/**
 * A calendar invite in a message (its text/calendar part, METHOD REQUEST or CANCEL; Google, Outlook, Apple, Bundle),
 * shown as a card above the body like Gmail's: the event as the organizer sent it, plus my side of it — my copy in
 * Bundle Calendar (added as "not answered" when the invite arrives), my answer and what it overlaps.
 */
export const MailInvite = z.object({
  method: z.enum(["request", "cancel"]),
  title: z.string().max(500),
  start: Timestamp,
  end: Timestamp,
  allDay: z.boolean(),
  /** The organizer's zone (DTSTART's TZID): "4:00 – 5:00 PM in São Paulo" when it isn't mine. */
  timeZone: z.string().max(64),
  location: z.string().max(500).optional(),
  /** Meet / Zoom / Teams link. */
  conferenceUrl: z.string().max(2000).optional(),
  organizer: MailAddress,
  /** Everyone invited but the organizer (at most 100; `guestCount` has the total). */
  guests: z.array(MailInviteGuest).max(100),
  guestCount: z.number().int().nonnegative(),
  /** A repeating event: answering asks "This event" or "All events". */
  recurring: z.boolean(),
  /** About one occurrence of a series only (its RECURRENCE-ID: the original start). */
  occurrence: Timestamp.optional(),
  /** The address it was sent to: I answer as this guest, from this address. */
  myAddress: EmailAddress,
  /** My answer (needs_action until I answer; for a repeating event, my answer to the series). */
  myResponse: RsvpStatus,
  /** My copy in Bundle Calendar (absent once cancelled or before it's added). */
  eventId: Id.optional(),
  /** An update that moved it: the time before, shown crossed out. */
  movedFrom: z.object({ start: Timestamp, end: Timestamp }).optional(),
  /** The organizer sent a newer version (or cancelled it) after this one: this card is out of date. */
  superseded: z.boolean().optional(),
  /** My other events it overlaps ("In your calendar: conflicts with …"). */
  conflicts: z.array(z.object({ eventId: Id, title: z.string().max(500), start: Timestamp, end: Timestamp, allDay: z.boolean() })).max(5),
  /** The .ics part (stays downloadable under the card). */
  attachmentId: Id,
  /**
   * The mailbox's domain when none of its addresses can send yet (ownership verified with the MX still at Google, no
   * DKIM / return-path): the card says answers can't be emailed instead of offering Yes / No / Maybe.
   */
  replyNotSetUp: z.string().max(253).optional(),
});
export type MailInvite = z.infer<typeof MailInvite>;

/** `scheduled`: waiting for its send time (scheduled send, or the undo-send window); cancelling makes it a draft again. */
export const MailMessageStatus = z.enum(["received", "scheduled", "queued", "sent", "delivered", "bounced", "complained", "failed"]);
export type MailMessageStatus = z.infer<typeof MailMessageStatus>;

export const MailMessage = z.object({
  id: Id,
  threadId: Id,
  direction: z.enum(["in", "out"]),
  from: MailAddress,
  to: z.array(MailAddress),
  cc: z.array(MailAddress),
  /** Only on messages this mailbox sent. */
  bcc: z.array(MailAddress).optional(),
  replyTo: MailAddress.optional(),
  subject: z.string(),
  /** Plain text (from text/plain, or derived from HTML). */
  text: z.string(),
  /** `text` is only its start (a `lite` thread read): `mailMessageText` has the rest. */
  textPartial: z.boolean().optional(),
  /** An HTML body exists: fetch it sanitized from `mailMessageBody`. */
  hasHtml: z.boolean(),
  /** Its remote images are blocked until "Show images" (Gmail: spam and suspicious mail); other mail's load, proxied. */
  remoteImages: z.boolean(),
  attachments: z.array(MailAttachment),
  auth: MailAuth.optional(),
  status: MailMessageStatus,
  /** When a `scheduled` message goes out. */
  sendAt: Timestamp.optional(),
  /** The RFC 5322 Message-ID. */
  messageIdHeader: z.string(),
  /** Delivered here through this group address (Gmail's "via group"); absent = sent to the mailbox itself. */
  via: EmailAddress.optional(),
  /** The address it was delivered to (Delivered-To, X-Original-To): one of mine for a Bcc, where To and Cc say nothing. */
  deliveredTo: EmailAddress.optional(),
  /** Its list's posting address (List-Post): Reply all includes it even if the list isn't in To or Cc. */
  listPost: EmailAddress.optional(),
  /** A calendar invite it carries (shown as the invite card). */
  invite: MailInvite.optional(),
  sentAt: Timestamp,
});
export type MailMessage = z.infer<typeof MailMessage>;

/** The same mail in another of my mailboxes (`MailThread.copies`). */
export const MailThreadCopy = z.object({ mailboxId: Id, threadId: Id });
export type MailThreadCopy = z.infer<typeof MailThreadCopy>;

export const MailThread = z.object({
  id: Id,
  mailboxId: Id,
  subject: z.string(),
  snippet: z.string(),
  tab: MailTabId,
  folder: MailFolder,
  /** Everyone on the thread except the mailbox itself, most recent sender first. */
  participants: z.array(MailAddress),
  messageCount: z.number().int().positive(),
  unread: z.boolean(),
  starred: z.boolean(),
  hasAttachments: z.boolean(),
  /** Classifier flags (absent until classified). */
  needsReply: z.boolean().optional(),
  phishing: z.boolean().optional(),
  /** A one-time code from the latest such mail, for 15 minutes. `code` null: the sender couldn't be verified. */
  verificationCode: z.object({ code: z.string().nullable(), expiresAt: Timestamp }).optional(),
  /** Snoozed: out of the inbox until then (Gmail's snooze). */
  snoozedUntil: Timestamp.optional(),
  /** The mailbox's labels on it. */
  labelIds: z.array(Id).optional(),
  /** The earliest of its messages waiting to be sent. */
  scheduledAt: Timestamp.optional(),
  /** When I last sent mail in it (not mail still scheduled): it's in Sent, wherever it is (Gmail's SENT label). */
  lastSentAt: Timestamp.optional(),
  /** "Remind me if no reply": when it comes back to the inbox unless someone answers first. */
  remindAt: Timestamp.optional(),
  /** Such a reminder came back unanswered: when I sent the message nobody has answered yet (until someone writes). */
  noReplySince: Timestamp.optional(),
  /** Why it's in Priority (absent = it isn't). */
  priority: MailPriorityReason.optional(),
  /** My mark on this conversation: Mark as priority (true) or Not priority (false); absent = none. */
  priorityMark: z.boolean().optional(),
  /** My mark on its sender, which their later mail follows; absent = none. */
  senderPriority: z.boolean().optional(),
  /** Unified list only: the group address its newest message came through (`MailMessage.via`), for the row's pill. */
  via: EmailAddress.optional(),
  /**
   * Unified list only: the same mail (the newest message's Message-ID) in my other mailboxes, collapsed into this row.
   * This row is the copy that opens; actions touch only it, and each copy keeps its own read state.
   */
  copies: z.array(MailThreadCopy).optional(),
  lastAt: Timestamp,
});
export type MailThread = z.infer<typeof MailThread>;

/** What a mailbox's list can show: a folder, or Gmail's views across folders (starred, snoozed, scheduled). */
export const MailListFolder = z.enum(["inbox", "starred", "snoozed", "scheduled", "sent", "archive", "trash", "spam"]);
export type MailListFolder = z.infer<typeof MailListFolder>;

/** A mailbox's label (Gmail's labels): any number per conversation, filterable from the rail. */
export const MailLabel = z.object({
  id: Id,
  mailboxId: Id,
  name: z.string().min(1).max(60),
  /** A tag colour token suffix (gray, brown, orange, yellow, green, blue, purple, pink, red). */
  color: z.string().max(20),
});
export type MailLabel = z.infer<typeof MailLabel>;

/** A message being written, saved as you type (Gmail's Drafts), mine only. */
export const MailDraft = z.object({
  id: Id,
  mailboxId: Id,
  mode: z.enum(["new", "reply", "forward"]),
  /** The From address chosen (one of the mailbox's `sendAs`); absent = its default. */
  from: EmailAddress.optional(),
  sourceMessageId: Id.optional(),
  threadId: Id.optional(),
  to: z.string().max(100_000),
  cc: z.string().max(100_000),
  bcc: z.string().max(100_000),
  subject: z.string().max(998),
  text: z.string().max(500_000),
  /** Files on it (uploaded, or kept from a send that was undone). */
  attachments: z.array(z.object({ fileId: Id, name: z.string().max(500), size: z.number().int().nonnegative() })).max(20).optional(),
  /** false: I left "Sent with Bundle" off this message (the composer's ✕); absent = it follows my setting. */
  sentWith: z.boolean().optional(),
  updatedAt: Timestamp,
});
export type MailDraft = z.infer<typeof MailDraft>;

/** "InfoMoney — Resgate já…": a tab's newest unread thread, its sender and subject. */
export const MailTabSummaryPreview = z.object({ sender: z.string(), subject: z.string() });
export type MailTabSummaryPreview = z.infer<typeof MailTabSummaryPreview>;

/** One tab of the strip: unread count, how many arrived since I last opened it, and who they're from. */
export const MailTabSummary = z.object({
  tab: MailTabId,
  unread: z.number().int().nonnegative(),
  newCount: z.number().int().nonnegative(),
  preview: MailTabSummaryPreview.optional(),
});
export type MailTabSummary = z.infer<typeof MailTabSummary>;

/** The Priority tab's entry in the strip: like a tab's, plus how many threads it has (Mail opens on it when any). */
export const MailPrioritySummary = MailTabSummary.omit({ tab: true }).extend({ total: z.number().int().nonnegative() });
export type MailPrioritySummary = z.infer<typeof MailPrioritySummary>;

/**
 * An address a mailbox can send as (Gmail's "Send mail as"): its own address, its alias addresses, the same names on
 * alias domains, addresses imported from Gmail's send-as settings and the groups it belongs to. Only addresses on the
 * workspace's domains that can send are listed (core logic/mailIdentity sendableAddresses).
 */
export const MailSendAs = z.object({
  address: EmailAddress,
  name: z.string().max(200).optional(),
  /** The From that new messages start with (exactly one entry). */
  default: z.boolean().optional(),
  /** A group address the mailbox is a member of. */
  group: z.boolean().optional(),
});
export type MailSendAs = z.infer<typeof MailSendAs>;

/**
 * Gmail's "When replying to a message": from the address the message was sent to ("same", the default), or always
 * from my default address ("default").
 */
export const MailReplyFrom = z.enum(["same", "default"]);
export type MailReplyFrom = z.infer<typeof MailReplyFrom>;

/**
 * My sending choices in a workspace (Settings → You › Mail): `defaultFrom`, the address new messages start with (one
 * of my mailboxes' `sendAs`; absent = each mailbox's own default), and `replyFrom` (absent = "same").
 */
export const MailSendSettings = z.object({ defaultFrom: EmailAddress.optional(), replyFrom: MailReplyFrom.optional() });
export type MailSendSettings = z.infer<typeof MailSendSettings>;

/** GET/PUT /v1/w/:ws/mail/send-settings (PUT replaces my settings: an absent field clears it). */
export const MailSendSettingsResponse = z.object({ settings: MailSendSettings });
export type MailSendSettingsResponse = z.infer<typeof MailSendSettingsResponse>;

export const Mailbox = z.object({
  id: Id,
  address: EmailAddress,
  displayName: z.string().max(200),
  /** Personal: one member's. Shared: several members (contato@, suporte@). */
  kind: z.enum(["personal", "shared"]),
  memberIds: z.array(Id),
  domainId: Id,
  unread: z.number().int().nonnegative(),
  /** Unread threads in its Priority (Mail's badge counts these). */
  priorityUnread: z.number().int().nonnegative().optional(),
  /** My signature for this mailbox, in the composer's markdown (no "-- " marker; core splitComposer/joinComposer). */
  signature: z.string().max(4000).optional(),
  /** Whether my signature starts new messages (Gmail's "For new emails use"); absent = yes. */
  signatureOnNew: z.boolean().optional(),
  /** Whether my signature goes into replies and forwards, above the quoted original; absent = yes. */
  signatureOnReply: z.boolean().optional(),
  /** Whether the "Sent with Bundle" line goes on my messages from this mailbox (core logic/mailFooter); absent = on. */
  sentWith: z.boolean().optional(),
  /** Other addresses that deliver here (vendas@, or the same name on an alias domain is implicit). Admins only. */
  aliases: z.array(EmailAddress).optional(),
  /** Outside addresses each message is forwarded to; `keepCopy` false = not kept in Bundle. Admins only. */
  forwardTo: z.array(EmailAddress).optional(),
  keepCopy: z.boolean().optional(),
  /** Members whose access comes from the Google Workspace import (Gmail delegation, or the group with its address). */
  googleMemberIds: z.array(Id).optional(),
  /**
   * A shared mailbox imported from Google: how many readers Google lists (accepted Gmail delegates, or the members of
   * `group`, the Google Group with its address, when it has no delegates) and how many of them aren't members here
   * yet (they get access when they join). `readers` 0 = Google lists no delegates: only admins can read it.
   */
  googleReaders: z.object({ readers: z.number().int().nonnegative(), pending: z.number().int().nonnegative(), group: EmailAddress.optional() }).optional(),
  /** The addresses I can send from with this mailbox (the composer's From); absent = only `address`. */
  sendAs: z.array(MailSendAs).optional(),
  /** Which From my replies start with (MailSendSettings.replyFrom, logic/mailIdentity replyFrom); absent = "same". */
  replyFrom: MailReplyFrom.optional(),
  createdAt: Timestamp,
});
export type Mailbox = z.infer<typeof Mailbox>;

export const MailContact = z.object({
  id: Id,
  email: EmailAddress,
  name: z.string().max(200).optional(),
  org: z.string().max(200).optional(),
  kind: ContactKind,
  /** Learned from mail (`auto`) or set by a person (`manual`, the classifier then keeps it). */
  source: z.enum(["auto", "manual"]),
  /** A workspace member with this address. */
  userId: Id.optional(),
  messageCount: z.number().int().nonnegative(),
  lastAt: Timestamp.optional(),
});
export type MailContact = z.infer<typeof MailContact>;

// ---------------------------------------------------------------------------------------------------------------
// Domains

export const DnsRecordType = z.enum(["MX", "TXT", "CNAME", "SRV"]);
export type DnsRecordType = z.infer<typeof DnsRecordType>;

/** What a record is for (the settings table groups and explains them). */
/** `clientSetup`: optional records that let mail apps set themselves up (autoconfig / autodiscover hosts, RFC 6186 SRV). */
export const DnsRecordPurpose = z.enum(["verify", "mx", "spf", "dkim", "returnPath", "dmarc", "mtaSts", "bimi", "mailFrom", "clientSetup"]);
export type DnsRecordPurpose = z.infer<typeof DnsRecordPurpose>;

export const DnsRecord = z.object({
  type: DnsRecordType,
  /** Relative to the domain: "@", "bundle1._domainkey", "_dmarc". */
  name: z.string(),
  value: z.string(),
  priority: z.number().int().optional(),
  /** SRV only: weight and port (`value` is the target host). */
  weight: z.number().int().optional(),
  port: z.number().int().optional(),
  purpose: DnsRecordPurpose,
  required: z.boolean(),
  /** Last check: found as expected, missing, or something else is there. */
  status: z.enum(["ok", "missing", "mismatch", "unchecked"]),
  /** What was found when it's a mismatch. */
  found: z.string().optional(),
  /**
   * DKIM (or the custom MAIL FROM's MX and SPF, purpose "mailFrom") found in DNS (`status` stays "unchecked") but not
   * yet verified by the service that sends our mail (Amazon SES): "pending" = it's checking; "failed" = it gave up (it does after 72 hours without the record) and Bundle
   * starts it again, at most hourly; "restarted" = Bundle started it again at `restartedAt` and it's checking.
   */
  verification: z.object({ state: z.enum(["pending", "failed", "restarted"]), restartedAt: Timestamp.optional() }).optional(),
});
export type DnsRecord = z.infer<typeof DnsRecord>;

export const MailDomainStatus = z.enum(["pending", "verified", "failed", "paused"]);
export type MailDomainStatus = z.infer<typeof MailDomainStatus>;

/** Where the domain's DNS is hosted (from its nameservers; core logic/dnsProviders). */
export const DnsProviderInfo = z.object({
  id: z.string(),
  name: z.string(),
  /** This domain's DNS records page at the provider. */
  manageUrl: z.string().optional(),
  /** The provider imports a BIND zone file (the settings card offers one to download, with where to import it). */
  zoneImport: z.boolean().optional(),
});
export type DnsProviderInfo = z.infer<typeof DnsProviderInfo>;

export const MailDomain = z.object({
  id: Id,
  domain: z.string(),
  provider: DnsProviderInfo.optional(),
  status: MailDomainStatus,
  /** Receiving works (MX points at us) and sending works (DKIM + return path verified). */
  canReceive: z.boolean(),
  canSend: z.boolean(),
  records: z.array(DnsRecord),
  dmarcPolicy: z.enum(["none", "quarantine", "reject"]),
  /** Google's domain alias: mail to anyone@this domain goes to the same name @ that domain (another of ours). */
  aliasOf: Id.optional(),
  /** Sending paused automatically: too many hard bounces or spam complaints lately. An admin resumes it. */
  sendPaused: z.object({ at: Timestamp, reason: z.enum(["bounces", "complaints"]) }).optional(),
  /** BIMI: the brand logo (SVG Tiny PS) we host and publish for the domain; its `default._bimi` record is in `records`. */
  bimi: z.object({ logoUrl: z.string() }).optional(),
  checkedAt: Timestamp.optional(),
  verifiedAt: Timestamp.optional(),
  createdAt: Timestamp,
});
export type MailDomain = z.infer<typeof MailDomain>;

// ---------------------------------------------------------------------------------------------------------------
// Requests and responses

/** GET /v1/w/:ws/mail/mailboxes — the mailboxes I can read. */
export const ListMailboxesResponse = z.object({ mailboxes: z.array(Mailbox) });
export type ListMailboxesResponse = z.infer<typeof ListMailboxesResponse>;

/** POST /v1/w/:ws/mail/mailboxes (admins) — `localPart`@domain. */
export const CreateMailboxRequest = z.object({
  domainId: Id,
  localPart: z.string().min(1).max(64),
  displayName: z.string().min(1).max(200),
  kind: z.enum(["personal", "shared"]),
  memberIds: z.array(Id).min(1).max(100),
});
export type CreateMailboxRequest = z.infer<typeof CreateMailboxRequest>;

/**
 * PATCH /v1/w/:ws/mail/mailboxes/:mailboxId — admins (name, members) or a member (my signature, where it goes, and the
 * "Sent with Bundle" line).
 */
export const UpdateMailboxRequest = z.object({
  displayName: z.string().min(1).max(200).optional(),
  memberIds: z.array(Id).min(1).max(100).optional(),
  signature: z.string().max(4000).nullable().optional(),
  signatureOnNew: z.boolean().optional(),
  signatureOnReply: z.boolean().optional(),
  sentWith: z.boolean().optional(),
});
export type UpdateMailboxRequest = z.infer<typeof UpdateMailboxRequest>;

export const MailboxResponse = z.object({ mailbox: Mailbox });
export type MailboxResponse = z.infer<typeof MailboxResponse>;

/** GET /v1/w/:ws/mail/mailboxes/:mailboxId/threads?folder&tab&q&cursor&limit */
export const ListMailThreadsQuery = z.object({
  folder: MailListFolder.default("inbox"),
  /** Only conversations with this label (any folder but trash and spam). */
  label: Id.optional(),
  /** Only for the inbox; absent = every tab. */
  tab: MailTabId.optional(),
  q: z.string().max(200).optional(),
  /**
   * The filter row: the same syntax as `q` (core logic/mailQuery), narrowing the folder and tab in view instead of
   * searching across them.
   */
  filter: z.string().max(400).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** "none": leave the strip's counts out (`tabs` empty, no `priority`): a search or a background list keeps the ones shown. */
  counts: z.literal("none").optional(),
});
export type ListMailThreadsQuery = z.infer<typeof ListMailThreadsQuery>;

export const ListMailThreadsResponse = z.object({
  threads: z.array(MailThread),
  /** Each category tab's counts. */
  tabs: z.array(MailTabSummary),
  /** The Priority tab's counts. */
  priority: MailPrioritySummary.optional(),
  /** The strip itself: the workspace's tabs, in order (see MailTabDef). */
  tabDefs: z.array(MailTabDef),
  nextCursor: z.string().optional(),
});
export type ListMailThreadsResponse = z.infer<typeof ListMailThreadsResponse>;

/** One search I ran (Enter, or a result opened). Mine alone: every device of mine reads it, nobody else can. */
export const MailSearchEntry = z.object({ id: Id, query: z.string().min(1).max(600), usedAt: Timestamp });
export type MailSearchEntry = z.infer<typeof MailSearchEntry>;

/** GET /v1/w/:ws/mail/search-history: newest first. */
export const ListMailSearchHistoryResponse = z.object({ entries: z.array(MailSearchEntry) });
export type ListMailSearchHistoryResponse = z.infer<typeof ListMailSearchHistoryResponse>;

/** POST /v1/w/:ws/mail/search-history: the same query again only moves to the top. */
export const SaveMailSearchRequest = z.object({ query: z.string().trim().min(1).max(600) });
export type SaveMailSearchRequest = z.infer<typeof SaveMailSearchRequest>;

/**
 * POST /v1/w/:ws/mail/messages/:messageId/invite — answer a calendar invite (Yes / No / Maybe): an iCalendar REPLY goes
 * to the organizer as `MailInvite.myAddress` (sent from it, else from the mailbox's sendable default), and my copy in
 * Bundle Calendar takes the answer. `scope` for a repeating event: "occurrence" answers just the invite's occurrence
 * (its RECURRENCE-ID, else the first one). 409 while nothing of the mailbox can send (`MailInvite.replyNotSetUp`).
 */
export const AnswerMailInviteRequest = z.object({
  response: z.enum(["accepted", "declined", "tentative"]),
  scope: z.enum(["series", "occurrence"]).optional(),
});
export type AnswerMailInviteRequest = z.infer<typeof AnswerMailInviteRequest>;

export const MailInviteResponse = z.object({ invite: MailInvite });
export type MailInviteResponse = z.infer<typeof MailInviteResponse>;

/** GET /v1/w/:ws/mail/threads/:threadId — the thread and its messages, oldest first. Opening marks it read. */
export const MailThreadResponse = z.object({ thread: MailThread, messages: z.array(MailMessage) });
export type MailThreadResponse = z.infer<typeof MailThreadResponse>;

/**
 * GET /v1/w/:ws/mail/threads/:threadId. `lite=1`: only the last messages carry their full text, so a long thread doesn't
 * read every body from storage; the others come `textPartial`. `fullAfter` (a reload): full text only from this time on.
 */
export const GetMailThreadQuery = z.object({ lite: z.enum(["0", "1"]).optional(), fullAfter: z.coerce.number().int().nonnegative().optional() });
export type GetMailThreadQuery = z.infer<typeof GetMailThreadQuery>;

/** GET /v1/w/:ws/mail/messages/:messageId/text: one message's full plain text. */
export const MailMessageTextResponse = z.object({ text: z.string() });
export type MailMessageTextResponse = z.infer<typeof MailMessageTextResponse>;

/**
 * PATCH /v1/w/:ws/mail/threads/:threadId — read/unread, star, move between folders, or move to another tab ("Move to
 * Clients"): the sender's later mail goes to that tab too.
 */
export const UpdateMailThreadRequest = z.object({
  unread: z.boolean().optional(),
  starred: z.boolean().optional(),
  folder: MailFolder.optional(),
  tab: MailTabId.optional(),
  /** Snooze until then; null wakes it now. */
  snoozeUntil: Timestamp.nullable().optional(),
  /** The labels on it, replaced whole. */
  labelIds: z.array(Id).max(20).optional(),
  /**
   * Mark as priority (true) or Not priority (false), for this conversation and its sender's later mail; null clears
   * both marks. Starring it clears a Not priority mark on the conversation.
   */
  priority: z.boolean().nullable().optional(),
});
export type UpdateMailThreadRequest = z.infer<typeof UpdateMailThreadRequest>;

export const MailThreadOnlyResponse = z.object({ thread: MailThread });
export type MailThreadOnlyResponse = z.infer<typeof MailThreadOnlyResponse>;

/** POST /v1/w/:ws/mail/mailboxes/:mailboxId/send — a new message, a reply (`replyToMessageId`) or a forward. */
export const SendMailRequest = z.object({
  to: z.array(MailAddress).max(500),
  cc: z.array(MailAddress).max(500).default([]),
  bcc: z.array(MailAddress).max(500).default([]),
  subject: z.string().max(998),
  text: z.string().max(500_000),
  html: z.string().max(2_000_000).optional(),
  replyToMessageId: Id.optional(),
  forwardOfMessageId: Id.optional(),
  /** Files already uploaded (POST /files) to attach. */
  fileIds: z.array(Id).max(20).default([]),
  /** Send later (scheduled send) or after the undo window; absent = now. */
  sendAt: Timestamp.optional(),
  /** "markdown": `text` is the composer's Slack-flavoured markdown, sent as HTML plus a plain-text version. */
  format: z.enum(["text", "markdown"]).optional(),
  /** The draft this was written in (deleted once sent). */
  draftId: Id.optional(),
  /** "Remind me if no reply": the conversation comes back to the inbox then, unless someone answers first. */
  remindAt: Timestamp.optional(),
  /**
   * Send as this address: one of the mailbox's `sendAs` (its domain must be able to send; 400 otherwise). Absent = the
   * mailbox's default From.
   */
  from: EmailAddress.optional(),
  /**
   * Add the "Sent with Bundle" line the composer showed (core logic/mailFooter#wantsSentWith): below my signature and
   * above the quoted original, in both parts, in my app language. Ignored when the workspace turned it off
   * (Workspace.mailSentWith); absent = no line.
   */
  sentWith: z.boolean().optional(),
  /** The language the composer showed that line in (the app's), so the message matches it; absent = my Member.locale. */
  sentWithLocale: Locale.optional(),
});
export type SendMailRequest = z.infer<typeof SendMailRequest>;

/** POST /v1/w/:ws/mail/mailboxes/:mailboxId/assist — Gmail's "Help me write": a description → the email body. */
/**
 * What the AI does: write a draft from `prompt` ("draft", the default), answer a free request about the draft
 * ("ask"), or change `current`: rewrite, shorten, lengthen, formal, friendly, fix (grammar and spelling), translate
 * (to `language`).
 */
export const MailAssistAction = z.enum(["draft", "ask", "rewrite", "shorten", "lengthen", "formal", "friendly", "fix", "translate"]);
export type MailAssistAction = z.infer<typeof MailAssistAction>;

export const MailAssistRequest = z.object({
  action: MailAssistAction.optional(),
  /** The description ("draft") or request ("ask"); the other actions don't need one. */
  prompt: z.string().trim().max(2000).default(""),
  /** "translate": the language to write in (a name or a BCP 47 tag). */
  language: z.string().max(40).optional(),
  /** What's written so far (improved or replaced). */
  current: z.string().max(20_000).optional(),
  /** Replying to this message (sent along as context). */
  replyToMessageId: Id.optional(),
});
export type MailAssistRequest = z.infer<typeof MailAssistRequest>;
export const MailAssistResponse = z.object({ text: z.string() });
export type MailAssistResponse = z.infer<typeof MailAssistResponse>;

/** A saved message I reuse ("Templates" in the composer): mine only. */
export const MailTemplate = z.object({
  id: Id,
  name: z.string().min(1).max(100),
  subject: z.string().max(998),
  text: z.string().max(100_000),
  updatedAt: Timestamp,
});
export type MailTemplate = z.infer<typeof MailTemplate>;
/** POST /v1/w/:ws/mail/templates, PATCH …/templates/:templateId */
export const SaveMailTemplateRequest = z.object({ name: z.string().trim().min(1).max(100), subject: z.string().max(998).default(""), text: z.string().max(100_000) });
export type SaveMailTemplateRequest = z.infer<typeof SaveMailTemplateRequest>;
export const MailTemplateResponse = z.object({ template: MailTemplate });
export type MailTemplateResponse = z.infer<typeof MailTemplateResponse>;
export const ListMailTemplatesResponse = z.object({ templates: z.array(MailTemplate) });
export type ListMailTemplatesResponse = z.infer<typeof ListMailTemplatesResponse>;

/** GET /v1/w/:ws/mail/mailboxes/:mailboxId/labels */
export const ListMailLabelsResponse = z.object({ labels: z.array(MailLabel) });
export type ListMailLabelsResponse = z.infer<typeof ListMailLabelsResponse>;
/** POST …/labels, PATCH /v1/w/:ws/mail/labels/:labelId */
export const SaveMailLabelRequest = z.object({ name: z.string().trim().min(1).max(60), color: z.string().max(20).default("gray") });
export type SaveMailLabelRequest = z.infer<typeof SaveMailLabelRequest>;
export const MailLabelResponse = z.object({ label: MailLabel });
export type MailLabelResponse = z.infer<typeof MailLabelResponse>;

/** GET /v1/w/:ws/mail/mailboxes/:mailboxId/drafts — my drafts there, newest first. */
export const ListMailDraftsResponse = z.object({ drafts: z.array(MailDraft) });
export type ListMailDraftsResponse = z.infer<typeof ListMailDraftsResponse>;
/** PUT /v1/w/:ws/mail/mailboxes/:mailboxId/drafts/:draftId — create or replace (the client picks the id). */
export const SaveMailDraftRequest = MailDraft.omit({ id: true, mailboxId: true, updatedAt: true });
export type SaveMailDraftRequest = z.infer<typeof SaveMailDraftRequest>;
export const MailDraftResponse = z.object({ draft: MailDraft });
export type MailDraftResponse = z.infer<typeof MailDraftResponse>;

export const SendMailResponse = z.object({ thread: MailThread, message: MailMessage });
export type SendMailResponse = z.infer<typeof SendMailResponse>;

/** GET /v1/w/:ws/mail/contacts?kind&q */
export const ListContactsQuery = z.object({ kind: ContactKind.optional(), q: z.string().max(200).optional() });
export type ListContactsQuery = z.infer<typeof ListContactsQuery>;

export const ListContactsResponse = z.object({ contacts: z.array(MailContact) });
export type ListContactsResponse = z.infer<typeof ListContactsResponse>;

/** POST /v1/w/:ws/mail/contacts */
export const CreateContactRequest = z.object({
  email: EmailAddress,
  name: z.string().max(200).optional(),
  org: z.string().max(200).optional(),
  kind: ContactKind,
});
export type CreateContactRequest = z.infer<typeof CreateContactRequest>;

/** PATCH /v1/w/:ws/mail/contacts/:contactId — setting `kind` makes it manual. */
export const UpdateContactRequest = z.object({
  name: z.string().max(200).nullable().optional(),
  org: z.string().max(200).nullable().optional(),
  kind: ContactKind.optional(),
});
export type UpdateContactRequest = z.infer<typeof UpdateContactRequest>;

export const ContactResponse = z.object({ contact: MailContact });
export type ContactResponse = z.infer<typeof ContactResponse>;

/**
 * A row of a projects database (one with a `contact` property, or one named Projects / Projetos without one yet) that
 * mail contacts can be linked to: its title, icon, status and the contacts in its first contact property.
 */
export const MailProject = z.object({
  rowId: Id,
  databaseId: Id,
  databaseTitle: z.string(),
  title: z.string(),
  icon: z.string().max(64).optional(),
  /** Its status option, when the database has a status property and the row has one. */
  status: z.object({ name: z.string(), color: z.string(), group: z.enum(["todo", "in_progress", "complete"]) }).optional(),
  contactIds: z.array(Id),
  /** I can change the row (link or unlink contacts). */
  canEdit: z.boolean(),
});
export type MailProject = z.infer<typeof MailProject>;

/** GET /v1/w/:ws/mail/projects — every project row I can see, done ones last. */
export const ListMailProjectsResponse = z.object({ projects: z.array(MailProject) });
export type ListMailProjectsResponse = z.infer<typeof ListMailProjectsResponse>;

/**
 * PUT / DELETE /v1/w/:ws/mail/contacts/:contactId/projects/:rowId — link a contact to a project row (its database gets
 * a "Clients" contact property first when it has none) or unlink it.
 */
export const MailProjectResponse = z.object({ project: MailProject });
export type MailProjectResponse = z.infer<typeof MailProjectResponse>;

/**
 * How a message body opens: the email as sent ("original", sanitized, as Gmail shows it; the default) or its reading
 * view ("readable": the article in the app's own colors, where the email has one).
 */
export type MailBodyView = "original" | "readable";

/**
 * GET/PUT /v1/w/:ws/mail/notifications — my mail preferences in this workspace: new-mail notifications (on or off,
 * whether Priority mail notifies, the default like Gmail's "High priority only", and which tabs notify whatever their
 * priority) and how messages open (`view`).
 */
export const MailNotifyPrefs = z.object({
  enabled: z.boolean(),
  /** Priority conversations notify, whatever their tab. Always sent back; a PUT without it keeps what's saved. */
  priority: z.boolean().optional(),
  tabs: z.array(MailTabId).max(20),
  /** How a message body opens (Settings → Email). Always sent back; a PUT without it keeps what's saved. */
  view: z.enum(["original", "readable"]).optional(),
});
export type MailNotifyPrefs = z.infer<typeof MailNotifyPrefs>;

export const MailNotifyPrefsResponse = z.object({ prefs: MailNotifyPrefs });
export type MailNotifyPrefsResponse = z.infer<typeof MailNotifyPrefsResponse>;

/** GET /v1/w/:ws/mail/domains (admins) */
export const ListMailDomainsResponse = z.object({ domains: z.array(MailDomain) });
export type ListMailDomainsResponse = z.infer<typeof ListMailDomainsResponse>;

/** POST /v1/w/:ws/mail/domains (admins) — starts onboarding; the response lists the records to add. */
/**
 * Who gets a mailbox automatically once the domain can receive mail: the admin adding it ("me"), every member
 * ("everyone": personal mailboxes at their own names) or nobody yet ("none").
 */
export const MailboxesFor = z.enum(["me", "everyone", "none"]);
export type MailboxesFor = z.infer<typeof MailboxesFor>;
export const AddMailDomainRequest = z.object({
  domain: z.string().min(3).max(253),
  mailboxes: MailboxesFor.default("me"),
  /** Add it as an alias of this domain of the workspace (Google's domain alias; the import suggests it). */
  aliasOf: Id.optional(),
});
export type AddMailDomainRequest = z.infer<typeof AddMailDomainRequest>;

/** PUT /v1/w/:ws/mail/mailboxes/:mailboxId/routing (admins) — its alias addresses and forwarding, replaced whole. */
export const UpdateMailboxRoutingRequest = z.object({
  aliases: z.array(EmailAddress).max(30),
  forwardTo: z.array(EmailAddress).max(10),
  keepCopy: z.boolean(),
});
export type UpdateMailboxRoutingRequest = z.infer<typeof UpdateMailboxRoutingRequest>;

/** PATCH /v1/w/:ws/mail/domains/:domainId (admins) — DMARC policy (served from our _dmarc record). */
export const UpdateMailDomainRequest = z.object({
  dmarcPolicy: z.enum(["none", "quarantine", "reject"]).optional(),
  /** Make this domain an alias of another of the workspace's domains; null makes it a domain of its own again. */
  aliasOf: Id.nullable().optional(),
  /** Resume sending after an automatic pause (`MailDomain.sendPaused`). */
  resumeSending: z.boolean().optional(),
  /** BIMI logo as SVG text (made SVG Tiny PS on the server); null removes it. */
  bimiSvg: z.string().max(65_536).nullable().optional(),
});
export type UpdateMailDomainRequest = z.infer<typeof UpdateMailDomainRequest>;

export const MailDomainResponse = z.object({ domain: MailDomain });
export type MailDomainResponse = z.infer<typeof MailDomainResponse>;

// ---------------------------------------------------------------------------------------------------------------
// Groups (Google Groups as distribution lists)

/**
 * Who may send to a group (Google Groups' "Who can post"): anyone (contato@, the default), only the organization
 * (senders on any of the workspace's domains) or only its members. Others get a bounce (550 5.7.1).
 */
export const MailGroupPostPolicy = z.enum(["anyone", "organization", "members"]);
export type MailGroupPostPolicy = z.infer<typeof MailGroupPostPolicy>;

export const MailGroupMember = z.object({
  address: EmailAddress,
  /** The workspace mailbox it delivers to; absent = an outside address (forwarded). */
  mailboxId: Id.optional(),
  role: z.enum(["owner", "manager", "member"]).optional(),
});
export type MailGroupMember = z.infer<typeof MailGroupMember>;

/** A group address on one of the workspace's domains: each message is delivered to every member. */
export const MailGroup = z.object({
  id: Id,
  address: EmailAddress,
  name: z.string().max(200),
  domainId: Id,
  whoCanPost: MailGroupPostPolicy,
  members: z.array(MailGroupMember),
  /** Imported from Google Workspace (kept in sync until an admin edits it here) or made in Bundle. */
  source: z.enum(["bundle", "google"]),
  /** An imported group an admin changed in Bundle: later syncs leave it alone ("edited in Bundle"). */
  edited: z.boolean().optional(),
  createdAt: Timestamp,
});
export type MailGroup = z.infer<typeof MailGroup>;

/** GET /v1/w/:ws/mail/groups (admins) */
export const ListMailGroupsResponse = z.object({ groups: z.array(MailGroup) });
export type ListMailGroupsResponse = z.infer<typeof ListMailGroupsResponse>;

/** A member to set: a workspace mailbox (`mailboxId`) or an outside address. */
export const MailGroupMemberInput = z.object({ address: EmailAddress.optional(), mailboxId: Id.optional(), role: z.enum(["owner", "manager", "member"]).optional() });
export type MailGroupMemberInput = z.infer<typeof MailGroupMemberInput>;

/** POST /v1/w/:ws/mail/groups (admins): `address` on one of the workspace's domains. */
export const CreateMailGroupRequest = z.object({
  address: EmailAddress,
  name: z.string().trim().max(200).default(""),
  whoCanPost: MailGroupPostPolicy.default("anyone"),
  members: z.array(MailGroupMemberInput).max(500).default([]),
});
export type CreateMailGroupRequest = z.infer<typeof CreateMailGroupRequest>;

/** PATCH /v1/w/:ws/mail/groups/:groupId (admins): members are replaced whole. */
export const UpdateMailGroupRequest = z.object({
  name: z.string().trim().max(200).optional(),
  whoCanPost: MailGroupPostPolicy.optional(),
  members: z.array(MailGroupMemberInput).max(500).optional(),
});
export type UpdateMailGroupRequest = z.infer<typeof UpdateMailGroupRequest>;

export const MailGroupResponse = z.object({ group: MailGroup });
export type MailGroupResponse = z.infer<typeof MailGroupResponse>;
